import { ZuruflexApiError } from '@zuruflex/api-client';
import { formatDateIn, toLocale } from '@zuruflex/i18n';
import type { OnboardingStep, OnboardingStepState, PartnerDocumentKind } from '@zuruflex/types';
import {
  Alert,
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  PageHeader,
  Skeleton,
  cn,
} from '@zuruflex/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BadgeCheck, Check, Clock, RotateCw, Send, ShieldAlert } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { DocumentRow } from '../components/DocumentRow.js';
import { DocumentUploadDialog } from '../components/DocumentUploadDialog.js';
import { api } from '../lib/api.js';
import { useAuth, usePartner } from '../lib/auth.js';

/**
 * Where each checklist step is actually done.
 *
 * `DOCUMENTS` is the one that has no page of its own — it is done in the
 * paperwork panel further down this same page. It used to be left with no
 * action at all, which made the one step that blocks every account the only
 * row a partner could not act on. It now scrolls to the panel and flashes
 * it, so the jump is something you can see happen rather than a page that
 * silently moved under you.
 */
const STEP_LINK: Record<OnboardingStep, string | null> = {
  // The agreement is accepted on the gate that stands in front of the whole
  // portal, so a partner who can see this row has already passed it — or is
  // about to be sent back to it on their next page load.
  AGREEMENT: null,
  PROFILE: '/settings',
  DOCUMENTS: null,
  PAYOUT: '/settings',
  VEHICLE: '/fleet',
};

/** Not required, but the one that most often settles a borderline review. */
const OPTIONAL_KINDS: readonly PartnerDocumentKind[] = ['PROOF_OF_ADDRESS'];

/** How long the paperwork panel stays ringed after the checklist jumps to it. */
const FLASH_MS = 1600;

/**
 * Everything standing between signing up and being live, on one page.
 *
 * It is one page rather than a wizard because none of it is ordered: a
 * partner waiting on an insurance certificate can set their payout account
 * meanwhile, and a wizard would have made them wait for no reason.
 *
 * The page is a wide two-column layout rather than one narrow column. The
 * work — the paperwork itself — takes the reading column on the left; where
 * the partner stands and what happens after they send rides alongside it on
 * the right, so the answer to "how much is left" does not have to be
 * scrolled back to after every upload.
 */
export function VerificationPage() {
  const { t, i18n } = useTranslation(['partner', 'common']);
  const locale = toLocale(i18n.language);
  const { partner } = usePartner();
  const { refreshPartner } = useAuth();
  const queryClient = useQueryClient();

  /**
   * Which document the upload dialog is open for.
   *
   * No car here any more: a car's registration, insurance and roadworthy
   * are edited on that car's own page. This screen is the partner's own
   * paperwork and nothing else.
   */
  const [uploading, setUploading] = useState<PartnerDocumentKind | null>(null);

  const paperwork = useRef<HTMLDivElement>(null);
  const [flashing, setFlashing] = useState(false);

  const onboarding = useQuery({
    queryKey: ['onboarding'],
    queryFn: () => api.myOnboarding(),
  });
  const documents = useQuery({
    queryKey: ['documents'],
    queryFn: () => api.myDocuments(),
  });
  const submit = useMutation({
    mutationFn: () => api.submitPartnerForReview(),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['onboarding'] }),
        refreshPartner(),
      ]);
    },
  });

  const showPaperwork = useCallback(() => {
    paperwork.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setFlashing(true);
  }, []);

  useEffect(() => {
    if (!flashing) return;
    const timer = window.setTimeout(() => setFlashing(false), FLASH_MS);
    return () => window.clearTimeout(timer);
  }, [flashing]);

  /*
   * A failed checklist is its own state, not a slow one. The two used to
   * share this branch, so a partner whose request fell over sat in front
   * of three grey boxes for as long as they cared to wait, with nothing to
   * press and no reason given.
   */
  if (!onboarding.isPending && !onboarding.data) {
    return (
      <>
        <PageHeader title={t('verify.title')} description={t('verify.subtitle')} />
        <Alert variant="danger" title={t('common:error.title')} className="max-w-xl">
          <p>{t('common:error.body')}</p>
          <Button
            variant="outline"
            size="sm"
            className="mt-3"
            onClick={() => void onboarding.refetch()}
          >
            <RotateCw />
            {t('common:action.retry')}
          </Button>
        </Alert>
      </>
    );
  }

  if (onboarding.isPending || !onboarding.data) {
    return (
      <>
        <PageHeader title={t('verify.title')} description={t('verify.subtitle')} />
        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_21rem]">
          <div className="flex flex-col gap-6">
            <Skeleton className="h-32 rounded-card" />
            <Skeleton className="h-64 rounded-card" />
          </div>
          <Skeleton className="h-80 rounded-card" />
        </div>
      </>
    );
  }

  const state = onboarding.data;
  const optional = OPTIONAL_KINDS.map((kind) => {
    const filed = (documents.data ?? [])
      .filter((document) => document.kind === kind && !document.vehicleId)
      .sort((a, b) => b.createdAt.localeCompare(a.createdAt));
    return {
      kind,
      state: filed[0] ? ('PENDING' as const) : ('MISSING' as const),
      document: filed[0] ?? null,
    };
  });

  return (
    <>
      <PageHeader
        title={t('verify.title')}
        description={t('verify.subtitle')}
        action={<StatusChip submittedAt={state.submittedAt} />}
      />

      <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_21rem] xl:gap-8">
        {/* ── The work ───────────────────────────────────── */}
        <div className="flex min-w-0 flex-col gap-6">
          <StatusPanel
            canSubmit={state.canSubmit}
            submittedAt={state.submittedAt}
            outstanding={state.steps.filter((step) => step.required && !step.done)}
            onSubmit={() => submit.mutate()}
            onShowPaperwork={showPaperwork}
            isSubmitting={submit.isPending}
            error={
              submit.error instanceof ZuruflexApiError
                ? submit.error.message
                : submit.error
                  ? t('common:error.unexpected')
                  : null
            }
          />

          <Card
            ref={paperwork}
            id="paperwork"
            className={cn(
              'scroll-mt-6 transition-shadow duration-300',
              flashing && 'ring-2 ring-accent ring-offset-2 ring-offset-surface',
            )}
          >
            <CardHeader>
              <div>
                <CardTitle>{t('verify.yourPaperwork')}</CardTitle>
                <CardDescription>{t('verify.yourPaperworkHint')}</CardDescription>
              </div>
            </CardHeader>
            <CardContent>
              <ul className="flex flex-col gap-2">
                {state.documents.map((requirement) => (
                  <DocumentRow
                    key={requirement.kind}
                    requirement={requirement}
                    onUpload={() => setUploading(requirement.kind)}
                  />
                ))}
                {optional.map((requirement) => (
                  <DocumentRow
                    key={requirement.kind}
                    requirement={requirement}
                    optional
                    onUpload={() => setUploading(requirement.kind)}
                  />
                ))}
              </ul>
            </CardContent>
          </Card>
        </div>

        {/* ── Where you stand ────────────────────────────── */}
        <aside className="flex flex-col gap-4 lg:sticky lg:top-8">
          <Checklist
            steps={state.steps}
            gathering={!state.submittedAt && partner.status === 'PENDING'}
            onShowPaperwork={showPaperwork}
          />
          <WhatHappensNext submittedAt={state.submittedAt} />
          {state.submittedAt ? (
            <p className="px-1 text-xs text-text-muted">
              {t('verify.submittedOn', { date: formatDateIn(state.submittedAt, locale) })}
            </p>
          ) : null}
        </aside>
      </div>

      {uploading ? (
        <DocumentUploadDialog kind={uploading} onClose={() => setUploading(null)} />
      ) : null}
    </>
  );
}

// ── Where you stand ──────────────────────────────────────────

/** The one-word answer, next to the title, before anything is read. */
function StatusChip({ submittedAt }: { submittedAt: string | null }) {
  const { t } = useTranslation(['partner', 'common']);
  const { partner } = usePartner();

  if (partner.status === 'APPROVED') {
    return (
      <Badge variant="success">
        <BadgeCheck /> {t('common:partnerStatus.APPROVED')}
      </Badge>
    );
  }
  if (partner.status === 'SUSPENDED' || partner.status === 'CLOSED') {
    return <Badge variant="danger">{t(`common:partnerStatus.${partner.status}`)}</Badge>;
  }
  if (submittedAt) {
    return (
      <Badge variant="info">
        <Clock /> {t('verify.chipWithUs')}
      </Badge>
    );
  }
  return <Badge variant="outline">{t('verify.chipDraft')}</Badge>;
}

function StatusPanel({
  canSubmit,
  submittedAt,
  outstanding,
  onSubmit,
  onShowPaperwork,
  isSubmitting,
  error,
}: {
  canSubmit: boolean;
  submittedAt: string | null;
  outstanding: OnboardingStepState[];
  onSubmit: () => void;
  onShowPaperwork: () => void;
  isSubmitting: boolean;
  error: string | null;
}) {
  const { t } = useTranslation(['partner', 'common']);
  const { partner } = usePartner();

  if (partner.status === 'APPROVED') {
    return <Alert variant="success">{t('verify.approved')}</Alert>;
  }
  if (partner.status === 'SUSPENDED' || partner.status === 'CLOSED') {
    return (
      <Alert variant="danger">
        <span className="font-semibold">{t('overview.suspendedTitle')}</span>
        {partner.statusReason ? <> — {partner.statusReason}</> : null}
      </Alert>
    );
  }
  if (submittedAt) {
    return <Alert variant="info">{t('verify.withUs')}</Alert>;
  }

  return (
    <div>
      {error ? (
        <Alert variant="danger" className="mb-3">
          {error}
        </Alert>
      ) : null}

      {/*
        Yellow only once everything is in. The panel going from a plain card
        to the one brand-yellow surface on the page is the moment the page
        has to sell — reaching it should look like arriving somewhere, not
        like the same box with a button that has stopped being grey.
      */}
      <Card variant={canSubmit ? 'brand' : 'default'}>
        <div className="flex flex-col gap-5 p-6 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex min-w-0 items-start gap-4">
            <span
              className={cn(
                'grid size-10 shrink-0 place-items-center rounded-pill',
                canSubmit
                  ? 'bg-accent-text/10 text-accent-text'
                  : 'bg-surface-sunken text-text-muted',
              )}
              aria-hidden="true"
            >
              {canSubmit ? <Send className="size-5" /> : <ShieldAlert className="size-5" />}
            </span>

            <div className="min-w-0">
              <h2 className="font-display text-lg font-bold">
                {canSubmit ? t('verify.readyTitle') : t('verify.notReadyTitle')}
              </h2>
              <p
                className={cn(
                  'mt-1 text-sm',
                  canSubmit ? 'text-accent-text/75' : 'text-text-muted',
                )}
              >
                {canSubmit ? t('verify.readyBody') : t('verify.notReadyBody')}
              </p>

              {/*
                A disabled button with nothing beside it is a dead end: it
                says no without saying what would make it yes. These name
                the blocking steps and go straight to them.
              */}
              {outstanding.length > 0 ? (
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <span className="text-xs font-semibold uppercase tracking-wider text-text-muted">
                    {t('verify.stillNeeded')}
                  </span>
                  {outstanding.map((step) => (
                    <StepShortcut key={step.step} step={step.step} onPaperwork={onShowPaperwork} />
                  ))}
                </div>
              ) : null}
            </div>
          </div>

          {/*
            Offered rather than hidden when it cannot be pressed: a button
            that appears only once everything is done leaves nothing on
            screen to tell somebody what they are working towards. Outline
            while it waits, though — a yellow button at 45% opacity reads
            as the page's primary action having broken.
          */}
          <Button
            className="shrink-0 self-start sm:self-auto"
            variant={canSubmit ? 'secondary' : 'outline'}
            disabled={!canSubmit || isSubmitting}
            isLoading={isSubmitting}
            onClick={onSubmit}
          >
            {isSubmitting ? t('common:state.working') : t('verify.submit')}
          </Button>
        </div>
      </Card>
    </div>
  );
}

/** A blocking step as a chip that goes where the step is done. */
function StepShortcut({ step, onPaperwork }: { step: OnboardingStep; onPaperwork: () => void }) {
  const { t } = useTranslation(['partner', 'common']);
  const label = t(`common:onboardingStep.${step}`);
  const to = STEP_LINK[step];
  const className =
    'rounded-pill border border-border-default bg-surface px-2.5 py-1 text-[11px] font-semibold text-text transition-colors hover:border-border-strong hover:bg-surface-hover';

  if (to) {
    return (
      <Link to={to} className={className}>
        {label}
      </Link>
    );
  }
  return (
    <button type="button" onClick={onPaperwork} className={className}>
      {label}
    </button>
  );
}

/**
 * The checklist, split by whether a step holds the review up.
 *
 * The progress line is the steps, not a percentage: a partner who is two
 * thirds of the way through wants to know which third is missing. The count
 * and the segments above it are still counted over the blocking steps only
 * — telling somebody they are "2 of 4" when the last two cannot stop
 * anything is how an account sits unsent over a bank account nobody needed
 * yet.
 */
function Checklist({
  steps,
  gathering,
  onShowPaperwork,
}: {
  steps: OnboardingStepState[];
  /** Still filling the form in, rather than waiting on Zuruflex or already in. */
  gathering: boolean;
  onShowPaperwork: () => void;
}) {
  const { t } = useTranslation(['partner', 'common']);
  const blocking = steps.filter((step) => step.required);
  const later = steps.filter((step) => !step.required);
  const done = blocking.filter((step) => step.done).length;

  return (
    <Card>
      <CardHeader>
        <div className="min-w-0">
          <CardTitle>{t('verify.steps')}</CardTitle>
          {/* The line describes work still to do, so it goes once there
              is none left to do — on an account already with Zuruflex it
              would be telling somebody to hurry up over nothing. */}
          {gathering ? <CardDescription>{t('verify.neededNow')}</CardDescription> : null}
        </div>
        <span className="shrink-0 font-display text-sm font-bold tabular-nums">
          {t('verify.doneOf', { done, total: blocking.length })}
        </span>
      </CardHeader>

      <CardContent className="space-y-4">
        <div className="flex gap-1.5" aria-hidden="true">
          {blocking.map((step) => (
            <span
              key={step.step}
              className={cn(
                'h-1.5 flex-1 rounded-pill transition-colors',
                step.done ? 'bg-success' : 'bg-surface-sunken',
              )}
            />
          ))}
        </div>

        <ul className="flex flex-col gap-1">
          {blocking.map((step) => (
            <ChecklistRow key={step.step} step={step} onShowPaperwork={onShowPaperwork} />
          ))}
        </ul>

        {later.length > 0 ? (
          <div className="border-t border-border-default pt-4">
            <p className="text-xs font-semibold uppercase tracking-wider text-text-muted">
              {t('verify.canWait')}
            </p>
            <ul className="mt-2 flex flex-col gap-1">
              {later.map((step) => (
                <ChecklistRow key={step.step} step={step} onShowPaperwork={onShowPaperwork} />
              ))}
            </ul>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}

function ChecklistRow({
  step,
  onShowPaperwork,
}: {
  step: OnboardingStepState;
  onShowPaperwork: () => void;
}) {
  const { t } = useTranslation(['partner', 'common']);
  const to = STEP_LINK[step.step];

  const body = (
    <>
      <span
        className={cn(
          'grid size-5 shrink-0 place-items-center rounded-pill border transition-colors',
          step.done
            ? 'border-success bg-success text-white'
            : 'border-border-strong bg-surface group-hover:border-text-muted',
        )}
        aria-hidden="true"
      >
        {step.done ? <Check className="size-3" strokeWidth={3} /> : null}
      </span>
      <span className="min-w-0 flex-1">
        <span className={cn('block truncate text-sm font-medium', step.done && 'text-text-muted')}>
          {t(`common:onboardingStep.${step.step}`)}
        </span>
        <span className="block truncate text-xs text-text-muted">
          {t(`verify.step.${step.step}`)}
        </span>
      </span>
    </>
  );

  const className =
    'group -mx-2 flex items-center gap-2.5 rounded-field px-2 py-1.5 text-left transition-colors';

  // A done step is a statement, not somewhere to go. Only what is left is
  // clickable, so the pointer is a promise that pressing changes something.
  if (step.done) {
    return (
      <li className={className}>
        {body}
        <span className="sr-only">{t('common:documentState.APPROVED')}</span>
      </li>
    );
  }

  return (
    <li>
      {to ? (
        <Link to={to} className={cn(className, 'flex w-[calc(100%+1rem)] hover:bg-surface-hover')}>
          {body}
        </Link>
      ) : (
        <button
          type="button"
          onClick={onShowPaperwork}
          className={cn(className, 'flex w-[calc(100%+1rem)] hover:bg-surface-hover')}
        >
          {body}
        </button>
      )}
    </li>
  );
}

/**
 * What the partner is actually waiting for.
 *
 * "We usually come back within a day" was only ever said in the one line
 * above the upload rows, where somebody who has already uploaded has no
 * reason to look again. Laid out as three stages with the current one
 * marked, the page answers "what now" without anybody having to ask.
 */
function WhatHappensNext({ submittedAt }: { submittedAt: string | null }) {
  const { t } = useTranslation(['partner', 'common']);
  const { partner } = usePartner();

  const stages = [
    { key: 'send', title: t('verify.next1'), body: t('verify.next1Body') },
    { key: 'check', title: t('verify.next2'), body: t('verify.next2Body') },
    { key: 'live', title: t('verify.next3'), body: t('verify.next3Body') },
  ];

  // Approved is past the last stage, not standing on it: marking "your cars
  // go live" as the stage in progress tells somebody who is already live
  // that they are still waiting for it.
  const at = partner.status === 'APPROVED' ? stages.length : submittedAt ? 1 : 0;

  return (
    <Card variant="muted">
      <CardHeader>
        <CardTitle>{t('verify.next')}</CardTitle>
      </CardHeader>
      <CardContent>
        <ol className="flex flex-col">
          {stages.map((stage, index) => {
            const passed = index < at;
            const here = index === at;
            return (
              <li key={stage.key} className="flex gap-3">
                {/* The rail is drawn per row rather than as one absolute
                    line behind them, so it stretches with whatever the
                    translated body wraps to. */}
                <div className="flex flex-col items-center">
                  <span
                    className={cn(
                      'grid size-6 shrink-0 place-items-center rounded-pill border text-[11px] font-bold tabular-nums',
                      passed && 'border-success bg-success text-white',
                      here && 'border-accent bg-accent text-accent-text',
                      !passed && !here && 'border-border-default bg-surface text-text-subtle',
                    )}
                    aria-hidden="true"
                  >
                    {passed ? <Check className="size-3" strokeWidth={3} /> : index + 1}
                  </span>
                  {index < stages.length - 1 ? (
                    <span
                      className={cn(
                        'w-px flex-1 transition-colors',
                        passed ? 'bg-success' : 'bg-border-default',
                      )}
                      aria-hidden="true"
                    />
                  ) : null}
                </div>

                <div className={cn('min-w-0', index < stages.length - 1 && 'pb-4')}>
                  <p className={cn('text-sm font-medium', !here && !passed && 'text-text-muted')}>
                    {stage.title}
                  </p>
                  <p className="mt-0.5 text-xs text-text-muted">{stage.body}</p>
                </div>
              </li>
            );
          })}
        </ol>
      </CardContent>
    </Card>
  );
}

// ── Each car's own paperwork ─────────────────────────────────
